import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AuthService } from '../auth/auth.service';
import {
  CreateGuardMaintenanceExecution,
  CreateGuardMaintenanceInspection,
  CreateGuardOperationalEvent,
  FieldOperationsAccess,
  FieldOperationResult,
  GuardFieldElement,
  GuardFieldElementType,
  GuardFieldPoint,
  GuardFieldOperationsService,
  GuardFieldOperationsView,
  GuardOperationalEvent,
  GuardOperationalEventPriority,
  GuardOperationalEventStatus,
  GuardOperationalEventType,
  GuardMaintenanceRecord
} from '../guard-field-operations.service';
import { GuardTake } from '../guard-takes.service';

interface IncidentDraft {
  type: GuardOperationalEventType;
  title: string;
  description: string;
  priority: GuardOperationalEventPriority;
  elementType: GuardFieldElementType | '';
  elementId: string;
}

interface MaintenanceDraft {
  isMaintenanceCompleted: boolean;
  observations: string;
  elementType: GuardFieldElementType | '';
  elementId: string;
  image?: NonNullable<CreateGuardMaintenanceInspection['image']>;
}

interface MaintenanceExecutionDraft {
  durationMinutes: string;
  backpackQuantity: string;
}

interface DateCorrectionDraft {
  target: 'inspection' | 'execution';
  requestedAt: string;
  reason: string;
}

@Component({
  selector: 'app-guard-field-operations',
  imports: [FormsModule],
  templateUrl: './guard-field-operations.component.html',
  styleUrl: './guard-field-operations.component.scss'
})
export class GuardFieldOperationsComponent implements OnInit, OnChanges {
  @Input() access: FieldOperationsAccess = 'guard';
  @Input() activeView: GuardFieldOperationsView = null;
  @Input() takes: GuardTake[] = [];
  @Input() selectedGuardTakeId = '';
  @Input() selectedMapPoint?: GuardFieldPoint;
  @Input() detectedMapElements: GuardFieldElement[] = [];
  @Input() pointSelectionActive = false;
  @Input() geolocationLoading = false;
  @Input() geolocationError = '';
  @Input() incidentsSummaryVisible = false;
  @Input() maintenanceSummaryVisible = false;

  @Output() activeViewChange = new EventEmitter<GuardFieldOperationsView>();
  @Output() summaryVisibilityChange = new EventEmitter<{
    view: Exclude<GuardFieldOperationsView, null>;
    visible: boolean;
  }>();
  @Output() sessionExpired = new EventEmitter<void>();
  @Output() statusChanged = new EventEmitter<string>();
  @Output() pointSelectionRequested = new EventEmitter<void>();
  @Output() geolocationRequested = new EventEmitter<void>();
  @Output() pointSelectionCancelled = new EventEmitter<void>();
  @Output() pointResetRequested = new EventEmitter<void>();

  private readonly authService = inject(AuthService);
  private readonly fieldOperationsService = inject(GuardFieldOperationsService);

  protected operationalEvents: GuardOperationalEvent[] = [];
  protected maintenanceEntries: GuardMaintenanceRecord[] = [];
  protected eventsLoading = true;
  protected maintenanceLoading = true;
  protected maintenanceImageLoading = false;
  protected eventsError = '';
  protected maintenanceError = '';
  protected feedbackMessage = '';
  protected formError = '';
  protected saving = false;
  protected statusSavingEventId?: number;
  protected statusUpdateError = '';
  protected incidentFormVisible = false;
  protected maintenanceFormVisible = false;
  protected executionFormRecordId?: number;
  protected dateCorrectionRecord?: GuardMaintenanceRecord;
  protected managerCorrectionIncident?: GuardOperationalEvent;
  protected managerCorrectedAt = '';
  protected workspaceExpanded = false;
  protected currentView: Exclude<GuardFieldOperationsView, null> = 'incidents';
  protected incidentSearch = '';
  protected maintenanceSearch = '';
  protected incidentStatusFilter: GuardOperationalEventStatus | 'all' = 'all';
  protected incidentTypeFilter: GuardOperationalEventType | 'all' = 'all';
  protected incidentDraft = this.createEmptyIncidentDraft();
  protected maintenanceDraft = this.createEmptyMaintenanceDraft();
  protected executionDraft = this.createEmptyExecutionDraft();
  protected dateCorrectionDraft = this.createEmptyDateCorrectionDraft();

  ngOnInit(): void {
    void Promise.all([this.loadOperationalEvents(), this.loadMaintenanceEntries()]);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['activeView']) {
      const view = changes['activeView'].currentValue as GuardFieldOperationsView;
      if (view) {
        this.currentView = view;
        this.setSummaryVisible(view, true);
      }

      this.workspaceExpanded = false;
      this.dismissForms();
    }

    if (changes['detectedMapElements'] && this.detectedMapElements.length) {
      const preferredElement = this.getPreferredDetectedElement();
      if (preferredElement) {
        this.setDraftElement(preferredElement);
      }
    }
  }

  protected get openIncidentCount(): number {
    return this.operationalEvents.filter((event) => event.status === 'Open' || event.status === 'InProgress').length;
  }

  protected get visibleOperationalEvents(): GuardOperationalEvent[] {
    const search = this.normalize(this.incidentSearch);

    return this.operationalEvents.filter((event) => {
      const matchesSearch = !search || this.normalize(`${event.title} ${event.description ?? ''} ${this.getElementLabel(event.elementType, event.elementId)}`).includes(search);
      const matchesStatus = this.incidentStatusFilter === 'all' || event.status === this.incidentStatusFilter;
      const matchesType = this.incidentTypeFilter === 'all' || event.type === this.incidentTypeFilter;
      return matchesSearch && matchesStatus && matchesType;
    });
  }

  protected get visibleMaintenanceEntries(): GuardMaintenanceRecord[] {
    const search = this.normalize(this.maintenanceSearch);
    if (!search) {
      return this.maintenanceEntries;
    }

    return this.maintenanceEntries.filter((entry) =>
      this.normalize(
        `${entry.observations ?? ''} ${this.getElementLabel(entry.elementType, entry.elementId)} ` +
        `${entry.isMaintenanceCompleted ? 'realizada' : 'no realizada'}`
      ).includes(search)
    );
  }

  protected openView(view: Exclude<GuardFieldOperationsView, null>): void {
    this.dismissForms();
    this.currentView = view;
    this.workspaceExpanded = true;
  }

  protected closeView(): void {
    this.dismissForms();
    this.workspaceExpanded = false;
  }

  protected closeSummary(view: Exclude<GuardFieldOperationsView, null>): void {
    this.setSummaryVisible(view, false);
    this.summaryVisibilityChange.emit({ view, visible: false });
    if (this.activeView === view) {
      const remainingView: GuardFieldOperationsView = this.incidentsSummaryVisible
        ? 'incidents'
        : (this.maintenanceSummaryVisible ? 'maintenance' : null);
      this.activeViewChange.emit(remainingView);
    }
  }

  private setSummaryVisible(view: Exclude<GuardFieldOperationsView, null>, visible: boolean): void {
    if (view === 'incidents') {
      this.incidentsSummaryVisible = visible;
    } else {
      this.maintenanceSummaryVisible = visible;
    }
  }

  protected showIncidentForm(): void {
    if (this.isManagerAccess()) {
      return;
    }

    this.dismissForms();
    this.incidentDraft = this.createEmptyIncidentDraft();
    this.pointResetRequested.emit();
    this.incidentFormVisible = true;
  }

  protected showMaintenanceForm(): void {
    if (this.isManagerAccess()) {
      return;
    }

    this.dismissForms();
    this.maintenanceDraft = this.createEmptyMaintenanceDraft();
    this.pointResetRequested.emit();
    this.maintenanceFormVisible = true;
  }

  protected dismissForms(): void {
    this.incidentFormVisible = false;
    this.maintenanceFormVisible = false;
    this.executionFormRecordId = undefined;
    this.dateCorrectionRecord = undefined;
    this.managerCorrectionIncident = undefined;
    this.formError = '';
  }

  protected startPointSelection(): void {
    this.formError = '';
    this.pointSelectionRequested.emit();
  }

  protected useCurrentLocation(): void {
    this.formError = '';
    this.geolocationRequested.emit();
  }

  protected cancelPointSelection(): void {
    this.pointSelectionCancelled.emit();
  }

  protected get elementTypeOptions(): { value: GuardFieldElementType; label: string }[] {
    return [
      { value: 'Canal', label: 'Acequia' },
      { value: 'SecondaryCanal', label: 'Acequia secundaria' },
      { value: 'Take', label: 'Toma' },
      { value: 'Parcel', label: 'Parcela' }
    ];
  }

  protected get incidentElementOptions(): GuardFieldElement[] {
    return this.getElementOptions(this.incidentDraft.elementType);
  }

  protected get maintenanceElementOptions(): GuardFieldElement[] {
    return this.getElementOptions(this.maintenanceDraft.elementType);
  }

  protected onIncidentElementTypeChanged(): void {
    this.incidentDraft.elementId = this.getDefaultElementId(this.incidentDraft.elementType);
  }

  protected onMaintenanceElementTypeChanged(): void {
    this.maintenanceDraft.elementId = this.getDefaultElementId(this.maintenanceDraft.elementType);
  }

  protected async onMaintenanceImageSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    this.maintenanceDraft.image = undefined;
    this.formError = '';

    if (!file) {
      return;
    }

    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      this.formError = 'La fotografía debe estar en formato JPG, PNG o WebP.';
      input.value = '';
      return;
    }

    if (file.size === 0 || file.size > 10 * 1024 * 1024) {
      this.formError = 'La fotografía no puede estar vacía ni superar los 10 MB.';
      input.value = '';
      return;
    }

    this.maintenanceImageLoading = true;
    try {
      this.maintenanceDraft.image = {
        fileName: file.name,
        contentType: file.type,
        contentBase64: await this.readFileAsBase64(file)
      };
    } catch {
      this.formError = 'No se ha podido leer la fotografía seleccionada.';
      input.value = '';
    } finally {
      this.maintenanceImageLoading = false;
    }
  }

  protected formatMapPoint(): string {
    if (!this.selectedMapPoint) {
      return '';
    }

    return this.formatPoint(this.selectedMapPoint);
  }

  protected formatPoint(point: GuardFieldPoint): string {
    return `X ${point.x.toFixed(1)} · Y ${point.y.toFixed(1)}`;
  }

  protected async submitIncident(): Promise<void> {
    const title = this.incidentDraft.title.trim();
    if (!title) {
      this.formError = 'Indica un título para el aviso o la incidencia.';
      return;
    }

    if (!this.validateLocationAndElement(this.incidentDraft.elementType, this.incidentDraft.elementId)) {
      return;
    }

    const payload: CreateGuardOperationalEvent = {
      type: this.incidentDraft.type,
      title,
      priority: this.incidentDraft.priority,
      elementType: this.incidentDraft.elementType,
      elementId: Number(this.incidentDraft.elementId),
      geometry: {
        x: this.selectedMapPoint!.x,
        y: this.selectedMapPoint!.y
      }
    };
    this.addOptionalText(payload, 'description', this.incidentDraft.description);

    this.saving = true;
    this.formError = '';
    const result = await this.fieldOperationsService.createOperationalEvent(payload, this.getAuthorizationHeaders());
    this.saving = false;

    if (!this.handleFailure(result, 'No se ha podido registrar la incidencia.')) {
      return;
    }

    this.operationalEvents = [result.value, ...this.operationalEvents];
    this.dismissForms();
    this.feedbackMessage = 'Incidencia registrada correctamente.';
    this.statusChanged.emit(this.feedbackMessage);
  }

  protected async submitMaintenanceEntry(): Promise<void> {
    if (this.maintenanceImageLoading) {
      this.formError = 'Espera a que termine de cargarse la fotografía.';
      return;
    }

    if (!this.validateLocationAndElement(this.maintenanceDraft.elementType, this.maintenanceDraft.elementId)) {
      return;
    }

    const payload: CreateGuardMaintenanceInspection = {
      elementType: this.maintenanceDraft.elementType,
      elementId: Number(this.maintenanceDraft.elementId),
      isMaintenanceCompleted: this.maintenanceDraft.isMaintenanceCompleted,
      geometry: {
        x: this.selectedMapPoint!.x,
        y: this.selectedMapPoint!.y
      }
    };
    this.addOptionalText(payload, 'observations', this.maintenanceDraft.observations);
    if (this.maintenanceDraft.image) {
      payload.image = this.maintenanceDraft.image;
    }

    this.saving = true;
    this.formError = '';
    const result = await this.fieldOperationsService.createMaintenanceInspection(
      payload,
      this.getAuthorizationHeaders()
    );
    this.saving = false;

    if (!this.handleFailure(result, 'No se ha podido guardar la escura.')) {
      return;
    }

    this.maintenanceEntries = [result.value, ...this.maintenanceEntries];
    this.dismissForms();
    this.feedbackMessage = this.maintenanceDraft.isMaintenanceCompleted
      ? 'Inspección guardada: la escura ya estaba realizada.'
      : 'Inspección guardada. Ya puedes registrar la ejecución de la escura.';
    this.statusChanged.emit(this.feedbackMessage);
  }

  protected showExecutionForm(record: GuardMaintenanceRecord): void {
    if (this.isManagerAccess() || record.isMaintenanceCompleted || record.execution) {
      return;
    }

    this.dismissForms();
    this.executionFormRecordId = record.id;
    this.executionDraft = this.createEmptyExecutionDraft();
  }

  protected async submitMaintenanceExecution(): Promise<void> {
    if (!this.executionFormRecordId) {
      return;
    }

    const durationMinutes = Number(this.executionDraft.durationMinutes);
    const backpackQuantity = Number(this.executionDraft.backpackQuantity);
    if (!Number.isInteger(durationMinutes) || durationMinutes <= 0 || durationMinutes % 15 !== 0) {
      this.formError = 'El tiempo empleado debe indicarse en tramos de 15 minutos.';
      return;
    }

    if (!Number.isFinite(backpackQuantity) || backpackQuantity < 0 ||
      !Number.isInteger(backpackQuantity * 4)) {
      this.formError = 'La mochila debe indicarse en incrementos de 0,25.';
      return;
    }

    const payload: CreateGuardMaintenanceExecution = { durationMinutes, backpackQuantity };
    this.saving = true;
    this.formError = '';
    const result = await this.fieldOperationsService.createMaintenanceExecution(
      this.executionFormRecordId,
      payload,
      this.getAuthorizationHeaders()
    );
    this.saving = false;

    if (!this.handleFailure(result, 'No se ha podido registrar la ejecución de la escura.')) {
      return;
    }

    this.maintenanceEntries = this.maintenanceEntries.map((record) =>
      record.id === result.value.id ? result.value : record
    );
    this.dismissForms();
    this.feedbackMessage = 'Ejecución de la escura registrada correctamente.';
    this.statusChanged.emit(this.feedbackMessage);
  }

  protected adjustExecutionDuration(delta: number): void {
    const currentValue = Number(this.executionDraft.durationMinutes);
    const nextValue = Math.max(15, (Number.isFinite(currentValue) ? currentValue : 15) + delta);
    this.executionDraft.durationMinutes = String(Math.round(nextValue / 15) * 15);
  }

  protected get executionDurationAtMinimum(): boolean {
    return Number(this.executionDraft.durationMinutes) <= 15;
  }

  protected adjustBackpackQuantity(delta: number): void {
    const currentValue = Number(this.executionDraft.backpackQuantity);
    const nextValue = Math.max(0, (Number.isFinite(currentValue) ? currentValue : 0) + delta);
    this.executionDraft.backpackQuantity = String(Math.round(nextValue * 4) / 4);
  }

  protected get backpackQuantityAtMinimum(): boolean {
    return Number(this.executionDraft.backpackQuantity) <= 0;
  }

  protected showDateCorrectionForm(
    record: GuardMaintenanceRecord,
    target: DateCorrectionDraft['target']
  ): void {
    if (this.isManagerAccess() || target === 'execution' && !record.execution) {
      return;
    }

    this.dismissForms();
    this.dateCorrectionRecord = record;
    this.dateCorrectionDraft = {
      ...this.createEmptyDateCorrectionDraft(),
      target
    };
  }

  protected async submitDateCorrectionIncident(): Promise<void> {
    const record = this.dateCorrectionRecord;
    if (!record) {
      return;
    }

    const requestedAt = new Date(this.dateCorrectionDraft.requestedAt);
    const reason = this.dateCorrectionDraft.reason.trim();
    if (Number.isNaN(requestedAt.getTime())) {
      this.formError = 'Indica la fecha y hora reales de la operación.';
      return;
    }

    if (!reason) {
      this.formError = 'Explica brevemente el motivo de la corrección.';
      return;
    }

    const isExecution = this.dateCorrectionDraft.target === 'execution';
    const operationLabel = isExecution ? 'ejecución' : 'inspección';
    const description = `Fecha y hora reales: ${requestedAt.toISOString()}. Motivo: ${reason}`;
    this.saving = true;
    this.formError = '';
    const result = await this.fieldOperationsService.createMaintenanceDateCorrectionIncident(
      record.id,
      `Corrección de fecha de ${operationLabel} de escura #${record.id}`,
      description,
      'Medium',
      { x: record.geometry.x, y: record.geometry.y },
      this.getAuthorizationHeaders(),
      isExecution ? record.execution?.id : undefined
    );
    this.saving = false;

    if (!this.handleFailure(result, 'No se ha podido registrar la incidencia de corrección.')) {
      return;
    }

    this.operationalEvents = [result.value, ...this.operationalEvents];
    this.dismissForms();
    this.feedbackMessage = 'Incidencia de corrección de fecha registrada correctamente.';
    this.statusChanged.emit(this.feedbackMessage);
  }

  protected getTakeLabel(elementId: number | null | undefined): string {
    if (!elementId) {
      return 'Sin toma asociada';
    }

    return this.takes.find((take) => take.backendId === elementId)?.label ?? `Toma nº${elementId}`;
  }

  protected getElementLabel(elementType: string | null | undefined, elementId: number | null | undefined): string {
    if (!elementType || !elementId) {
      return 'Sin elemento asociado';
    }

    if (elementType === 'Take') {
      return this.getTakeLabel(elementId);
    }

    const detectedElement = this.detectedMapElements.find(
      (element) => element.type === elementType && element.id === elementId
    );
    if (detectedElement) {
      return detectedElement.label;
    }

    const typeLabel = this.elementTypeOptions.find((option) => option.value === elementType)?.label ?? 'Elemento';
    return `${typeLabel} nº${elementId}`;
  }

  protected getEventTypeLabel(type: GuardOperationalEventType): string {
    return type === 'Incident' ? 'Incidencia' : 'Aviso';
  }

  protected getPriorityLabel(priority: GuardOperationalEventPriority): string {
    return { Low: 'Baja', Medium: 'Media', High: 'Alta' }[priority];
  }

  protected getStatusLabel(status: GuardOperationalEventStatus): string {
    return {
      Open: 'Abierta',
      InProgress: 'En curso',
      Resolved: 'Resuelta',
      Cancelled: 'Cancelada'
    }[status];
  }

  protected isMaintenanceDateCorrectionIncident(event: GuardOperationalEvent): boolean {
    return event.type === 'Incident' &&
      (event.elementType === 'MaintenanceInspection' || event.elementType === 'MaintenanceExecution') &&
      !!event.elementId;
  }

  protected showManagerDateCorrectionForm(event: GuardOperationalEvent): void {
    if (!this.isManagerAccess() || !this.isMaintenanceDateCorrectionIncident(event)) {
      return;
    }

    this.dismissForms();
    this.managerCorrectionIncident = event;
    const match = event.description?.match(/Fecha y hora reales:\s*([^\.]+(?:\.\d+)?Z)/i);
    const requestedAt = match?.[1] ? new Date(match[1]) : undefined;
    this.managerCorrectedAt = requestedAt && !Number.isNaN(requestedAt.getTime())
      ? this.formatLocalDateTimeInput(requestedAt)
      : '';
  }

  protected async submitManagerDateCorrection(): Promise<void> {
    const incident = this.managerCorrectionIncident;
    const targetType = incident?.elementType;
    const targetId = incident?.elementId;
    const correctedAt = new Date(this.managerCorrectedAt);
    if (!incident ||
      (targetType !== 'MaintenanceInspection' && targetType !== 'MaintenanceExecution') ||
      !targetId) {
      return;
    }

    if (Number.isNaN(correctedAt.getTime())) {
      this.formError = 'Indica la fecha y hora correctas.';
      return;
    }

    this.saving = true;
    this.formError = '';
    const result = await this.fieldOperationsService.applyOperationDateCorrection(
      targetType,
      targetId,
      incident.id,
      correctedAt.toISOString(),
      this.getAuthorizationHeaders()
    );
    this.saving = false;

    if (!this.handleFailure(result, 'No se ha podido aplicar la corrección de fecha.')) {
      return;
    }

    this.dismissForms();
    await this.loadMaintenanceEntries();
    this.feedbackMessage = 'Fecha efectiva corregida; la fecha original se conserva en el historial.';
    this.statusChanged.emit(this.feedbackMessage);
  }

  protected isManagerAccess(): boolean {
    return this.access === 'manager';
  }

  protected async updateOperationalEventStatus(
    event: GuardOperationalEvent,
    select: HTMLSelectElement
  ): Promise<void> {
    if (!this.isManagerAccess()) {
      return;
    }

    const status = select.value as GuardOperationalEventStatus;
    if (status === event.status) {
      return;
    }

    this.statusSavingEventId = event.id;
    this.statusUpdateError = '';
    const result = await this.fieldOperationsService.updateOperationalEventStatus(
      event.id,
      status,
      this.getAuthorizationHeaders()
    );
    this.statusSavingEventId = undefined;

    if (result.kind === 'unauthorized') {
      select.value = event.status;
      this.sessionExpired.emit();
      return;
    }

    if (result.kind !== 'ready') {
      select.value = event.status;
      this.statusUpdateError = result.kind === 'invalid'
        ? result.message
        : 'No se ha podido actualizar el estado de la incidencia.';
      this.statusChanged.emit(this.statusUpdateError);
      return;
    }

    this.operationalEvents = this.operationalEvents.map((current) =>
      current.id === result.value.id ? result.value : current
    );
    this.feedbackMessage = `Estado actualizado a ${this.getStatusLabel(result.value.status).toLowerCase()}.`;
    this.statusChanged.emit(this.feedbackMessage);
  }

  protected formatDate(value: string): string {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return new Intl.DateTimeFormat('es-ES', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  }

  protected trackById(_: number, item: GuardOperationalEvent | GuardMaintenanceRecord): number {
    return item.id;
  }

  private async loadOperationalEvents(): Promise<void> {
    this.eventsLoading = true;
    this.eventsError = '';
    const result = await this.fieldOperationsService.getOperationalEvents(
      this.getAuthorizationHeaders(),
      this.access
    );
    this.eventsLoading = false;

    if (!this.handleLoadFailure(result, 'No se han podido cargar los avisos e incidencias.')) {
      this.eventsError = result.kind === 'invalid' ? result.message : 'No se han podido cargar los avisos e incidencias.';
      return;
    }

    this.operationalEvents = result.value.items;
  }

  private async loadMaintenanceEntries(): Promise<void> {
    this.maintenanceLoading = true;
    this.maintenanceError = '';
    const result = await this.fieldOperationsService.getMaintenanceRecords(
      this.getAuthorizationHeaders(),
      this.access
    );
    this.maintenanceLoading = false;

    if (!this.handleLoadFailure(result, 'No se han podido cargar las escuras.')) {
      this.maintenanceError = result.kind === 'invalid' ? result.message : 'No se han podido cargar las escuras.';
      return;
    }

    this.maintenanceEntries = result.value.items;
  }

  private handleLoadFailure<T>(result: FieldOperationResult<T>, unavailableMessage: string): result is { kind: 'ready'; value: T } {
    if (result.kind === 'unauthorized') {
      this.sessionExpired.emit();
      return false;
    }

    if (result.kind === 'unavailable') {
      this.statusChanged.emit(unavailableMessage);
      return false;
    }

    return result.kind === 'ready';
  }

  private handleFailure<T>(result: FieldOperationResult<T>, unavailableMessage: string): result is { kind: 'ready'; value: T } {
    if (result.kind === 'ready') {
      return true;
    }

    if (result.kind === 'unauthorized') {
      this.sessionExpired.emit();
      return false;
    }

    this.formError = result.kind === 'invalid' ? result.message : unavailableMessage;
    this.statusChanged.emit(this.formError);
    return false;
  }

  private getAuthorizationHeaders(): Record<string, string> {
    return this.authService.getAuthorizationHeaders(() => this.sessionExpired.emit());
  }

  private addOptionalText<T extends object, K extends keyof T>(target: T, key: K, value: string): void {
    const normalized = value.trim();
    if (normalized) {
      target[key] = normalized as T[K];
    }
  }

  private readFileAsBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(reader.error);
      reader.onload = () => {
        const result = reader.result;
        if (!(result instanceof ArrayBuffer)) {
          reject(new Error('El fichero no se ha leído como datos binarios.'));
          return;
        }

        const bytes = new Uint8Array(result);
        let binary = '';
        const chunkSize = 8192;
        for (let offset = 0; offset < bytes.length; offset += chunkSize) {
          binary += String.fromCharCode(...bytes.subarray(offset, offset + chunkSize));
        }

        resolve(btoa(binary));
      };
      reader.readAsArrayBuffer(file);
    });
  }

  private createEmptyIncidentDraft(): IncidentDraft {
    return {
      type: 'Incident',
      title: '',
      description: '',
      priority: 'Medium',
      elementType: this.selectedGuardTakeId ? 'Take' : '',
      elementId: this.selectedGuardTakeId
    };
  }

  private createEmptyMaintenanceDraft(): MaintenanceDraft {
    return {
      isMaintenanceCompleted: false,
      observations: '',
      elementType: this.selectedGuardTakeId ? 'Take' : '',
      elementId: this.selectedGuardTakeId
    };
  }

  private createEmptyExecutionDraft(): MaintenanceExecutionDraft {
    return {
      durationMinutes: '15',
      backpackQuantity: '0'
    };
  }

  private createEmptyDateCorrectionDraft(): DateCorrectionDraft {
    return {
      target: 'inspection',
      requestedAt: '',
      reason: ''
    };
  }

  private formatLocalDateTimeInput(date: Date): string {
    const pad = (value: number): string => value.toString().padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
      `T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  }

  private normalize(value: string): string {
    return value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
  }

  private getElementOptions(type: GuardFieldElementType | ''): GuardFieldElement[] {
    if (!type) {
      return [];
    }

    const options: GuardFieldElement[] = type === 'Take'
      ? this.takes.map((take) => ({ type: 'Take' as const, id: take.backendId, label: take.label }))
      : [];

    this.detectedMapElements
      .filter((element) => element.type === type)
      .forEach((element) => {
        if (!options.some((option) => option.id === element.id)) {
          options.push(element);
        }
      });

    return options.sort((left, right) => left.label.localeCompare(right.label, 'es'));
  }

  private getDefaultElementId(type: GuardFieldElementType | ''): string {
    const options = this.getElementOptions(type);
    return options.length === 1 ? `${options[0].id}` : '';
  }

  private getPreferredDetectedElement(): GuardFieldElement | undefined {
    const priority: GuardFieldElementType[] = ['Take', 'SecondaryCanal', 'Canal', 'Parcel'];
    return [...this.detectedMapElements].sort(
      (left, right) => priority.indexOf(left.type) - priority.indexOf(right.type)
    )[0];
  }

  private setDraftElement(element: GuardFieldElement): void {
    if (this.incidentFormVisible) {
      this.incidentDraft.elementType = element.type;
      this.incidentDraft.elementId = `${element.id}`;
    }

    if (this.maintenanceFormVisible) {
      this.maintenanceDraft.elementType = element.type;
      this.maintenanceDraft.elementId = `${element.id}`;
    }
  }

  private validateLocationAndElement(type: GuardFieldElementType | '', id: string): boolean {
    if (!this.selectedMapPoint) {
      this.formError = 'Marca en el mapa el punto exacto del registro.';
      return false;
    }

    if (!type) {
      this.formError = 'Selecciona el tipo de elemento relacionado.';
      return false;
    }

    if (!id || !Number.isInteger(Number(id)) || Number(id) <= 0) {
      this.formError = 'No se ha identificado el elemento en ese punto. Vuelve a marcarlo sobre el elemento o elige otra instancia.';
      return false;
    }

    return true;
  }
}
